import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChatCubeProps {
    isOpen: boolean;
    toggleOpen: () => void;
}

export function ChatCube({ isOpen, toggleOpen }: ChatCubeProps) {
    // Cube rotation animation
    // We rotate the container endlessly when closed, face outward when open.
    const cubeVariants = {
        initial: {
            rotateX: 15,
            rotateY: 0,
            rotateZ: 10,
        },
        spinning: {
            rotateX: 15,
            rotateY: [0, 360],
            rotateZ: 10,
            transition: {
                duration: 20,
                repeat: Infinity,
                ease: "linear" as const,
            },
        },
        open: {
            rotateX: 0,
            rotateY: 0,
            rotateZ: 0,
            transition: {
                duration: 0.4,
                ease: "easeOut" as const,
            },
        },
        hover: {
            scale: 1.1,
            transition: { duration: 0.3 }
        },
        tap: {
            scale: 0.95,
            transition: { duration: 0.1 }
        }
    };

    // Face size is 3rem (w-12) = 48px.
    // Translate Z should be half of that = 24px.
    const translateZ = "24px";

    const faceClasses = cn(
        "absolute inset-0 w-full h-full flex items-center justify-center",
        // Glass background with subtle gradient
        "bg-linear-to-br from-sapphire-500/10 to-sapphire-600/10 backdrop-blur-[2px]",
        // Sophisticated border and glow
        "border border-white/20",
        "shadow-[inset_0_0_15px_rgba(59,130,246,0.2)]", // Inner blue glow
        // Ensure visibility
        "[backface-visibility:visible]"
    );

    return (
        <div className="w-12 h-12 [perspective:1000px] z-50">
            <motion.div
                onClick={toggleOpen}
                variants={cubeVariants}
                initial="initial"
                animate={isOpen ? "open" : "spinning"}
                whileHover="hover"
                whileTap="tap"
                className="w-full h-full relative [transform-style:preserve-3d] cursor-pointer"
            >
                {/* Glowing Core */}
                <div className="absolute inset-4 bg-sapphire-400/30 blur-md rounded-full animate-pulse"
                    style={{ transform: 'translateZ(0)' }}
                />

                {/* Front Face */}
                <div
                    className={faceClasses}
                    style={{ transform: `rotateY(0deg) translateZ(${translateZ})` }}
                >
                    <AnimatePresence mode="wait">
                        {isOpen ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
                    </AnimatePresence>
                </div>

                {/* Back Face */}
                <div
                    className={faceClasses}
                    style={{ transform: `rotateY(180deg) translateZ(${translateZ})` }}
                >
                    <MessageCircle className="w-6 h-6 text-white" />
                </div>

                {/* Right Face */}
                <div
                    className={faceClasses}
                    style={{ transform: `rotateY(90deg) translateZ(${translateZ})` }}
                >
                    <MessageCircle className="w-6 h-6 text-white" />
                </div>

                {/* Left Face */}
                <div
                    className={faceClasses}
                    style={{ transform: `rotateY(-90deg) translateZ(${translateZ})` }}
                >
                    <MessageCircle className="w-6 h-6 text-white" />
                </div>

                {/* Top Face */}
                <div
                    className={faceClasses}
                    style={{ transform: `rotateX(90deg) translateZ(${translateZ})` }}
                >
                    <MessageCircle className="w-6 h-6 text-white" />
                </div>

                {/* Bottom Face */}
                <div
                    className={faceClasses}
                    style={{ transform: `rotateX(-90deg) translateZ(${translateZ})` }}
                >
                    <MessageCircle className="w-6 h-6 text-white" />
                </div>
            </motion.div>
        </div>
    );
}
